import {
  PASTE_COMMAND,
  createCommand,
  LexicalCommand,
  $getSelection,
  $isRangeSelection,
  DOMConversionMap,
  DOMExportOutput,
  NodeKey,
  Spread,
  $applyNodeReplacement,
  $nodesOfType,
  $createTextNode,
  LexicalNode,
  ElementFormatType,
  $splitNode,
  $isTextNode,
  ElementNode,
  COMMAND_PRIORITY_NORMAL,
  COMMAND_PRIORITY_HIGH,
} from "lexical";
import {mergeRegister} from "@lexical/utils";
import {useEffect, useRef} from "react";
import {useLexicalComposerContext} from "@lexical/react/LexicalComposerContext";
import {CreateImageNodePayload} from "./ToyImagePlugin";
import {DSSpinner} from "../DSIcon/DSSpinner";
import {Box, css, Row} from "../Box/Box";
import {
  DecoratorBlockNode,
  SerializedDecoratorBlockNode,
} from "@lexical/react/LexicalDecoratorBlockNode";

const PASTE_FILES_COMMAND: LexicalCommand<File[]> = createCommand();

type FileInfo = {img: {width: number; height: number} | null; name: string; src: string};

type SerializedSpinnerNode = Spread<{processId: number}, SerializedDecoratorBlockNode>;

export class SpinnerNode extends DecoratorBlockNode {
  __processId: number;

  static getType(): string {
    return "spinner";
  }

  static clone(node: SpinnerNode): SpinnerNode {
    return new SpinnerNode(node.__processId, node.__format, node.__key);
  }

  static importJSON(serializedNode: SerializedSpinnerNode): SpinnerNode {
    return $createSpinnerNode({processId: serializedNode.processId});
  }

  createDOM(): HTMLElement {
    const el = document.createElement("div");
    return el;
  }

  updateDOM(): false {
    return false;
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement("div");
    // making it inline-block allows placing a cursor before or after
    // needed if image is only element in editor
    element.classList.add(css({display: "inline-block"}));
    element.setAttribute("data-lexical-cdx-spinner-process-id", this.__processId.toString());
    return {element};
  }

  static importDOM(): DOMConversionMap | null {
    return {
      div: (domNode: HTMLElement) => {
        if (!domNode.hasAttribute("data-lexical-cdx-spinner-process-id")) {
          return null;
        }
        return {
          conversion: (innerNode: HTMLElement) => {
            const processId = innerNode.getAttribute("data-lexical-cdx-spinner-process-id");
            if (processId) {
              const node = $createSpinnerNode({processId: Number(processId)});
              return {node};
            } else {
              return null;
            }
          },
          priority: 1,
        };
      },
    };
  }

  constructor(processId: number, format?: ElementFormatType, key?: NodeKey) {
    super(format, key);
    this.__processId = processId;
  }

  exportJSON(): SerializedSpinnerNode {
    return {
      processId: this.__processId,
      type: this.__type,
      version: 1,
      format: this.__format,
    };
  }

  decorate(): JSX.Element {
    return (
      <Row align="start">
        <Box colorTheme="gray50" bg="foreground" pa="16px" rounded={4}>
          <DSSpinner size={24} />
        </Box>
      </Row>
    );
  }
}

export interface SpinnerPayload {
  processId: number;
  key?: NodeKey;
  format?: ElementFormatType;
}

export function $createSpinnerNode({processId, format, key}: SpinnerPayload): SpinnerNode {
  return $applyNodeReplacement(new SpinnerNode(processId, format, key));
}

let nextProcessId = 1;

const CdxPasteImagePlugin = (props: {
  onFile: (file: File) => Promise<FileInfo>;
  createNodeFn: (args: CreateImageNodePayload) => LexicalNode;
}) => {
  const refs = useRef(props);
  useEffect(() => {
    refs.current = props;
  });
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    return mergeRegister(
      editor.registerCommand(
        PASTE_COMMAND,
        (event) => {
          if (event instanceof ClipboardEvent) {
            const dataTransfer = event.clipboardData;
            const hasFiles = dataTransfer?.types.includes("Files");
            if (hasFiles) {
              const files = Array.from(dataTransfer!.files);
              editor.dispatchCommand(PASTE_FILES_COMMAND, files);
              return true;
            }
          }
          return false;
        },
        COMMAND_PRIORITY_HIGH
      ),
      editor.registerCommand(
        PASTE_FILES_COMMAND,
        (files) => {
          if (files.length === 0) return false;
          editor.update(() => {
            for (const file of files) {
              const processId = (nextProcessId += 1);
              const selection = $getSelection();
              if (!$isRangeSelection(selection)) return;
              let splitNode: ElementNode;
              let splitOffset: number;
              const focusNode = selection.focus.getNode();
              const focusOffset = selection.focus.offset;
              if ($isTextNode(focusNode)) {
                splitNode = focusNode.getParentOrThrow();
                splitOffset = focusNode.getIndexWithinParent();
                if (focusOffset > 0) {
                  splitOffset += 1;
                }
              } else {
                splitNode = focusNode;
                splitOffset = focusOffset;
              }
              const [, rightTree] = $splitNode(splitNode, splitOffset);
              rightTree.insertBefore($createSpinnerNode({processId}));
              rightTree.selectStart();

              refs.current.onFile(file).then(
                ({name, img, src}) => {
                  editor.update(() => {
                    const processed = new Set<string>();
                    // $nodesOfType returns duplicates!?
                    for (const node of $nodesOfType(SpinnerNode)) {
                      if (node.__processId !== processId || processed.has(node.__key)) continue;
                      processed.add(node.__key);
                      if (img) {
                        node.replace(refs.current.createNodeFn({altText: name, ...img, src}));
                      } else {
                        node.replace($createTextNode(`[${name}](${src})`));
                      }
                    }
                  });
                },
                (err) => {
                  editor.update(() => {
                    const processed = new Set<string>();
                    for (const node of $nodesOfType(SpinnerNode)) {
                      if (node.__processId !== processId || processed.has(node.__key)) continue;
                      processed.add(node.__key);
                      node.replace($createTextNode(`Failed embedding file: ${err}`));
                    }
                  });
                }
              );
            }
          });
          return true;
        },
        COMMAND_PRIORITY_NORMAL
      )
    );
  }, [editor]);
  return null;
};

export default CdxPasteImagePlugin;
